import clsx from 'clsx';
import type { ReactElement, ReactNode } from 'react';
import {
    cloneElement,
    isValidElement,
    useCallback,
    useId,
    useRef,
} from 'react';
import { useEventListener } from 'usehooks-ts';

import classes from './SmlTooltip.module.css';

import { useCSSAnchorPosition } from '~/components/ui/Util/useCSSAnchorPosition';
import { getElementRef, useForkRef } from '~/components/ui/Util/useForkRef';

export interface SmlTooltipProps {
    /**
     * Text shown as a tooltip. Only for simple text. No formatting. No HTML.
     */
    text: ReactNode;
    children: ReactNode;

    /**
     * Set to false to render the child element, but disable the tooltip.
     * Can be useful to make the tooltip conditionally available.
     */
    enabled?: boolean;
    className?: string;
}

/**
 * SmartLearn Tooltip component.
 *
 * @example
 * ```tsx
 * <SmlTooltip text="Help text">
 *   <button>Hover me</button>
 * </SmlTooltip>
 * ```
 */
export function SmlTooltip({
    text,
    children,
    enabled = true,
    className,
}: SmlTooltipProps) {
    if (!enabled) {
        return <>{children}</>;
    }

    return (
        <SmlTooltipInner text={text} className={className}>
            {children}
        </SmlTooltipInner>
    );
}

function SmlTooltipInner({ text, children, className }: SmlTooltipProps) {
    const popoverRef = useRef<HTMLDivElement | null>(null);
    const anchorRef = useRef<HTMLElement | null>(null);
    const hoverTimeoutRef = useRef<number | null>(null);
    const existingAnchorNameRef = useRef<string | null>(null);
    const defaultId = useId();
    const tooltipId = `sml-tooltip-${defaultId}`;

    const { setAnchorElement, setPositionedElement } = useCSSAnchorPosition<
        HTMLElement,
        HTMLDivElement
    >(`--sml-tooltip-${tooltipId}`);

    const child = isValidElement(children) ? children : <span>{children}</span>;
    const childElement = child as ReactElement;
    const childRef = getElementRef(childElement);

    // We add native event listeners for mouseenter, mouseleave, hover and blur events for having
    // hover tooltips (there is no ready to use native invokers API for that other
    // than an open proposal). We also reuse any anchor names already on the child passed
    // for the tooltip (there can only be one anchor-name per element).
    const setAnchor = useCallback(
        (node: HTMLElement | null) => {
            anchorRef.current = node;

            if (node) {
                // Defer anchor-name check to next frame to allow other components
                // (like SmlMenu) to set their anchor first
                requestAnimationFrame(() => {
                    // Check if the element already has an anchor-name set
                    // First check inline style, then computed style
                    let existingAnchorName =
                        node.style.getPropertyValue('anchor-name');

                    if (!existingAnchorName || existingAnchorName === 'none') {
                        const computedStyle = window.getComputedStyle(node);
                        existingAnchorName =
                            computedStyle.getPropertyValue('anchor-name');
                    }

                    if (existingAnchorName && existingAnchorName !== 'none') {
                        // Reuse the existing anchor name instead of creating a new one
                        existingAnchorNameRef.current = existingAnchorName;
                        // Update positioned element if it already exists
                        if (popoverRef.current) {
                            popoverRef.current.style.setProperty(
                                'position-anchor',
                                existingAnchorName,
                            );
                        }
                    } else {
                        // No existing anchor, set our own
                        existingAnchorNameRef.current = null;
                        setAnchorElement(node);
                    }
                });
            }
        },
        [setAnchorElement],
    );

    const handleMouseEnter = useCallback(() => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }

        hoverTimeoutRef.current = window.setTimeout(() => {
            popoverRef.current?.showPopover();
        }, 300);
    }, []);

    const handleMouseLeave = useCallback(() => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
        popoverRef.current?.hidePopover();
    }, []);

    const handleFocus = useCallback(() => {
        popoverRef.current?.showPopover();
    }, []);

    const handleBlur = useCallback(() => {
        popoverRef.current?.hidePopover();
    }, []);

    useEventListener('mouseenter', handleMouseEnter, anchorRef);
    useEventListener('mouseleave', handleMouseLeave, anchorRef);
    useEventListener('focus', handleFocus, anchorRef);
    useEventListener('blur', handleBlur, anchorRef);

    const mergedRef = useForkRef<HTMLElement>(childRef, setAnchor);

    const enhancedChild = cloneElement(childElement, {
        ref: mergedRef,
        title: undefined, // Remove browser native tooltip to prevent overlap with custom tooltip
    });

    return (
        <>
            {enhancedChild}
            <div
                id={tooltipId}
                ref={(node) => {
                    popoverRef.current = node;
                    // If we already detected an existing anchor, use it
                    // Otherwise use the normal positioned element setup
                    if (existingAnchorNameRef.current && node) {
                        node.style.setProperty(
                            'position-anchor',
                            existingAnchorNameRef.current,
                        );
                    } else {
                        setPositionedElement(node);
                    }
                }}
                popover={'hint'}
                role="tooltip"
                className={clsx(classes.smlTooltip, className)}
            >
                {text}
            </div>
        </>
    );
}
